import React, { useState } from 'react';
import { 
  Plus, 
  Save, 
  Activity, 
  Thermometer, 
  Scale, 
  Wind, 
  Heart,
  Droplet,
  Salad,
  Baby
} from 'lucide-react';
import { AppointmentSummaryResponse } from './types';
import { PatientService } from '../../../services/api/patient.service';
import { AddPatientVitalsDTO, UpdatePatientVitalsDTO } from '../../../services/api/types';
import toast from 'react-hot-toast';

interface VitalsTabProps {
  data: AppointmentSummaryResponse['appointmentSummary'];
  reload: () => void;
}

const VitalsTab: React.FC<VitalsTabProps> = ({ data, reload }) => {
  const { appointment, appointmentSummary: nestedSummary } = data;
  const { vitals, patient } = nestedSummary;
  const existingVitals = vitals[0];
  const isFemale = patient.gender.toLowerCase() === 'female';

  const [formData, setFormData] = useState<AddPatientVitalsDTO>({
    patientId: patient.id,
    appointmentId: appointment.id,
    patientConcerns: appointment.patientConcern,
    weight: existingVitals?.weight || undefined,
    height: existingVitals?.height || undefined,
    temperature: existingVitals?.temperature || undefined,
    pulseRate: existingVitals?.pulseRate || undefined,
    heartRate: existingVitals?.heartRate || undefined,
    spo2: existingVitals?.spo2 || undefined,
    glucose: existingVitals?.glucose || undefined,
    respiratoryRate: existingVitals?.respiratoryRate || undefined,
    vegetarian: existingVitals?.vegetarian || false,
    bloodPressureMin: existingVitals?.bloodPressureMin || undefined,
    bloodPressureMax: existingVitals?.bloodPressureMax || undefined,
    pregnancyMonths: existingVitals?.pregnancyMonths || undefined,
    fetalHeartRate: existingVitals?.fetalHeartRate || undefined,
  });

  const [saving, setSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
             type === 'number' ? (value === "" ? undefined : Number(value)) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let response;
      if (existingVitals?.id) {
        const updateData: UpdatePatientVitalsDTO = {
          vitalsId: existingVitals.id,
          data:formData
        };
        response = await PatientService.updatePatientVitalsV2(updateData);
      } else {
        response = await PatientService.addPatientVitalsV2(formData);
      }

      if (response.success) {
        toast.success(response.message || "Vitals saved successfully");
        reload();
      } else {
        toast.error(response.message || "Failed to save vitals");
      }
    } catch (error) {
      toast.error("An error occurred while saving vitals");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="flex items-center justify-between border-b border-gray-100 pb-6">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-emerald-600" />
          <h2 className="text-xl font-bold text-gray-900">Patient Vitals</h2>
        </div>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-md disabled:bg-gray-400"
        >
          {saving ? <Plus className="h-5 w-5 animate-spin" /> : (existingVitals ? <Save className="h-5 w-5" /> : <Plus className="h-5 w-5" />)}
          {saving ? 'Saving...' : (existingVitals ? 'Update Vitals' : 'Add Vitals')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <VitalsInput label="Weight (kg)" name="weight" value={formData.weight} onChange={handleChange} icon={Scale} />
        <VitalsInput label="Height (cm)" name="height" value={formData.height} onChange={handleChange} icon={Activity} />
        <VitalsInput label="Temperature (°C)" name="temperature" value={formData.temperature} onChange={handleChange} icon={Thermometer} />
        <VitalsInput label="Pulse Rate (bpm)" name="pulseRate" value={formData.pulseRate} onChange={handleChange} icon={Activity} />
        <VitalsInput label="Heart Rate (bpm)" name="heartRate" value={formData.heartRate} onChange={handleChange} icon={Heart} />
        <VitalsInput label="SpO2 (%)" name="spo2" value={formData.spo2} onChange={handleChange} icon={Wind} />
        <VitalsInput label="Glucose (mg/dL)" name="glucose" value={formData.glucose} onChange={handleChange} icon={Droplet} />
        <VitalsInput label="Respiratory Rate" name="respiratoryRate" value={formData.respiratoryRate} onChange={handleChange} icon={Wind} />
        <VitalsInput label="BP Min (Diastolic)" name="bloodPressureMin" value={formData.bloodPressureMin} onChange={handleChange} icon={Activity} />
        <VitalsInput label="BP Max (Systolic)" name="bloodPressureMax" value={formData.bloodPressureMax} onChange={handleChange} icon={Activity} />
        
        {isFemale && (
          <>
            <VitalsInput label="Pregnancy Months" name="pregnancyMonths" value={formData.pregnancyMonths} onChange={handleChange} icon={Baby} />
            <VitalsInput label="Fetal Heart Rate" name="fetalHeartRate" value={formData.fetalHeartRate} onChange={handleChange} icon={Heart} />
          </>
        )}

        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100 h-full">
          <Salad className="h-5 w-5 text-emerald-600" />
          <label className="text-sm font-medium text-gray-700 flex-1">Vegetarian</label>
          <input
            type="checkbox"
            name="vegetarian"
            checked={formData.vegetarian}
            onChange={handleChange}
            className="w-5 h-5 accent-emerald-600 rounded"
          />
        </div>
      </div>
    </form>
  );
};

const VitalsInput = ({ label, name, value, onChange, icon: Icon }: any) => (
  <div className="space-y-2 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-white hover:border-emerald-200 transition-all shadow-sm">
    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
      <Icon className="h-4 w-4 text-emerald-600" />
      {label}
    </label>
    <input
      type="number"
      name={name}
      value={value === undefined ? "" : value}
      onChange={onChange}
      step="any"
      placeholder="0.00"
      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all font-bold text-gray-900"
    />
  </div>
);

export default VitalsTab;
